import React, { useMemo } from "react";
import { Box, Typography, useTheme } from "@mui/material";
import { ComposableMap, Geographies, Geography, Marker } from "react-simple-maps";
import { alpha } from "@mui/material/styles";
import { coordinatesForCountry } from "./countryCoordinates";

const GEO_URL = "https://cdn.jsdelivr.net/npm/world-atlas@2.0.2/countries-110m.json";

export default function WorldSessionsMap({ countries = [] }) {
    const theme = useTheme();

    const markers = useMemo(() => {
        const list = Array.isArray(countries) ? countries : [];
        const max = Math.max(1, ...list.map((c) => Number(c.sessions) || 0));
        return list
            .map((c) => {
                const coordinates = coordinatesForCountry(c.countryCode);
                if (!coordinates) return null;
                const sessions = Number(c.sessions) || 0;
                const t = sessions / max;
                return {
                    countryCode: c.countryCode,
                    coordinates,
                    sessions,
                    r: 4 + t * 14,
                };
            })
            .filter(Boolean);
    }, [countries]);

    const landFill = theme.palette.mode === "dark" ? alpha(theme.palette.common.white, 0.06) : alpha(theme.palette.common.black, 0.06);
    const landStroke = theme.palette.mode === "dark" ? alpha(theme.palette.common.white, 0.1) : alpha(theme.palette.common.black, 0.08);
    const dotColor = theme.palette.primary.main;

    if (!markers.length) {
        return (
            <Typography variant="body2" color="text.secondary" sx={{ py: 4, textAlign: "center" }}>
                No location data to map yet.
            </Typography>
        );
    }

    return (
        <Box sx={{ width: "100%", "& svg": { width: "100%", height: "auto", display: "block" } }}>
            <ComposableMap
                projection="geoEqualEarth"
                projectionConfig={{ scale: 120, center: [0, 10] }}
                style={{ width: "100%", height: "auto" }}
            >
                <Geographies geography={GEO_URL}>
                    {({ geographies }) =>
                        geographies.map((geo) => (
                            <Geography
                                key={geo.rsmKey}
                                geography={geo}
                                fill={landFill}
                                stroke={landStroke}
                                strokeWidth={0.4}
                                style={{
                                    default: { outline: "none" },
                                    hover: { outline: "none", fill: alpha(dotColor, 0.12) },
                                    pressed: { outline: "none" },
                                }}
                            />
                        ))
                    }
                </Geographies>
                {markers.map((m) => (
                    <Marker key={m.countryCode} coordinates={m.coordinates}>
                        <g>
                            <title>{`${m.countryCode}: ${m.sessions} sessions`}</title>
                            <circle r={m.r + 2} fill={dotColor} fillOpacity={0.15} />
                            <circle r={m.r} fill={dotColor} fillOpacity={0.7} stroke={theme.palette.background.paper} strokeWidth={1} />
                        </g>
                    </Marker>
                ))}
            </ComposableMap>
        </Box>
    );
}
